import React, { useState, useEffect } from 'react';
import { Modal,Form, Input, Select, Button, message, Tooltip, Affix, Card } from 'antd';
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useQuill } from "react-quilljs";
import 'react-quill/dist/quill.snow.css';

const { TextArea } = Input;
const { Option } = Select;

const CreateEmailTemplate = ({ fetchEmailTemplate, template, onClose, visible }) => {
  const [form] = Form.useForm();
  const { quill, quillRef } = useQuill();
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [objectList, setObjectList] = useState([]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [lookupFields, setLookupFields] = useState([]);
  const [selectedLookupField, setSelectedLookupField] = useState(null);
 

  useEffect(() => {
    if (quill) {
      quill.on('text-change', () => {
        const updatedBody = quill.root.innerHTML;
        setBody(updatedBody);
        form.setFieldsValue({ body: updatedBody });
      });
      if (template?.body) {
        quill.clipboard.dangerouslyPasteHTML(template.body); // Properly set initial content
        setBody(template.body);
      }
    }
  }, [quill, template]);
  

  // Fetch the list of objects on component mount
  useEffect(() => {
    const fetchObjects = async () => {
      const apiServiceForObjects = new ApiService(
        `${BASE_URL}/mt_objects`,
        { 'Content-Type': 'application/json' },
        'GET'
      );

      try {
        setLoading(true);
        const response = await apiServiceForObjects.makeCall();
        setObjectList(response);
      } catch (error) {
        message.error('Failed to fetch objects.');
      } finally {
        setLoading(false);
      }
    };

    fetchObjects();
  }, []);

  // Fetch fields based on selected object
  useEffect(() => {
    if (!selectedObject) return;

    const fetchFields = async () => {
      const apiServiceForFields = new ApiService(
        `${BASE_URL}/mt_fields/object/${selectedObject}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );

      try {
        setLoading(true);
        const response = await apiServiceForFields.makeCall();
        
        setAvailableFields(response);
      } catch (error) {
        message.error('Failed to fetch fields.');
      } finally {
        setLoading(false);
      }
    };

    fetchFields();
  }, [selectedObject]);

  // Handle callout for lookup field type
  useEffect(() => {
    if (!selectedField) return;

    const selectedFieldDetails = availableFields.find((field) => field.name === selectedField);

    // Trigger callout if the field type is "lookup"
    if (selectedFieldDetails?.type === 'lookup') {
      const parentObjectName = selectedFieldDetails.parent_object_name;

      const fetchLookupFields = async () => {
        const apiServiceForLookup = new ApiService(
          `${BASE_URL}/mt_fields/object/${parentObjectName}`,
          { 'Content-Type': 'application/json' },
          'GET'
        );

        try {
          setLoading(true);
          const response = await apiServiceForLookup.makeCall();
          setLookupFields(response); // Update fields dynamically
        } catch (error) {
          message.error('Failed to fetch lookup fields.');
        } finally {
          setLoading(false);
        }
      };

      fetchLookupFields();
    }
  }, [selectedField]);

  // Update the Merge Field Value dynamically
  useEffect(() => {
    if (selectedObject && selectedField) {
        let mergeField = `$${selectedField}`;
        if (selectedLookupField) {
          mergeField += `.${selectedLookupField}`;
        }
    
        form.setFieldsValue({
          mergeFieldValue: `{${mergeField}}`,
        });
      }
  }, [selectedObject, selectedField, selectedLookupField, form]);

  useEffect(() => {
    if (template) {
        setSelectedObject(template.object_name);
      form.setFieldsValue({
        templateName: template.template_name,
        object: template.object_name,
        subject: template.subject,
        description: template.description,
        
      });
      setBody(template.body || ''); 
    } else {
      form.resetFields();
      setBody('');
    }
  }, [template, form]);

  const handleSubmit = async (values) => {

    const body = {
        email_template:
        {      
      template_name: values.templateName,
      object_name: values.object,
      subject: values.subject,
      body: values.body,
      description: values.description,
        }
    };


    try {
        const method = template ? 'PATCH' : 'POST';
        const url = template
          ? `${BASE_URL}/edit_template/${template._id}`
          : `${BASE_URL}/create_template`;
    
        const apiService = new ApiService(
          url,
          { 'Content-Type': 'application/json' },
          method,
          body
        );
    
        const response = await apiService.makeCall();
    
        message.success(`Email template ${template ? 'updated' : 'created'} successfully!`);
        onClose(); // Close the drawer
        fetchEmailTemplate(); // Refresh data
      } catch (error) {
        console.error(`Error ${template ? 'updating' : 'creating'} email template:`, error);
        message.error(`Failed to ${template ? 'update' : 'create'} email template. Please try again.`);
      }
  };

  

  const handleCancel = () => {
    onClose();
  };



  return (
    <div >
      <Card style={{maxHeight: '700px', overflowY: 'auto'}}>
      <h1 style={{ textAlign: 'left', marginBottom: '20px' }}>{template ? 'Edit Template' : 'Create New Template'}</h1>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          templateName: '',
          mergeFieldValue: '',
          subject: '',
          body: '',
        }}
      >
        {/* Select Object */}
        <Form.Item
          label="Select Object"
          name="object"
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 10 }}
        //   rules={[{ required: true, message: 'Please select an object!' }]}
        >
          <Select
            placeholder="Select Object"
            style={{ color: '#bfbfbf' }}
            loading={loading}
            allowClear
            disabled={loading || !objectList.length}
            onChange={(value) => {
              setSelectedObject(value); // Update selected object state
              setSelectedField(null); // Reset the selected field
              form.resetFields(['field']); // Reset the field value in the form
              form.resetFields(['mergeFieldValue']);
            }}
          >
            {objectList.map((obj) => (
              <Option key={obj._id} value={obj.name}>
                {obj.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Select Field */}
        <Form.Item
          label="Select Field"
          name="field"
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 10 }}
        >
          <Select
            placeholder="Select Field"
            style={{ color: '#bfbfbf' }}
            allowClear
            disabled={!selectedObject || !availableFields.length}
            onChange={(value) => {
                setSelectedField(value);
                // Reset lookup fields if the selected field is not of type "lookup"
                const selectedFieldDetails = availableFields.find((field) => field.name === value);
                if (selectedFieldDetails?.type !== 'lookup') {
                    setLookupFields([]); // Clear lookup fields
                }
                setSelectedLookupField(null);
                form.resetFields(['lookupField']);
                form.resetFields(['mergeFieldValue']);
            }
            } // Update selected field state
          >
            {availableFields.map((field) => (
              <Option key={field._id} value={field.name}>
                {field.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Render Lookup Fields If Selected Field is a Lookup */}
        {selectedObject && selectedField && lookupFields.length > 0 && (
        <Form.Item
            label="Lookup Fields"
            name="lookupField"
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 10 }}
            // rules={[{ required: true, message: 'Please select a lookup field!' }]}
        >
            <Select
            placeholder="Select Lookup Field"
            style={{ color: '#bfbfbf' }}
            allowClear
            onChange={(value) => {
                setSelectedLookupField(value);
                form.resetFields(['mergeFieldValue']);
            }
            }
            >
            {lookupFields.map((field) => (
                <Option key={field._id} value={field.name}>
                {field.label}
                </Option>
            ))}
            </Select>
        </Form.Item>
        )}

        {/* Merge Field Value */}
        <Form.Item
           label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              Merge Field Value
              <Tooltip title="Copy and paste the merge field value into your template below.">
                <InfoCircleOutlined
                  className="anticon anticon-info-circle"
                  style={{ fontSize: '16px', color: '#1890ff', cursor: 'pointer' }}
                />
              </Tooltip>
            </div>
          }
          name="mergeFieldValue"
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 10 }}
        >
          <Input placeholder="Merge Field Value" readOnly />
        </Form.Item>

        <h3 style={{ textAlign: 'left', marginBottom: '20px' }}>Email Template Information</h3>

        {/* Template Name */}
        <Form.Item
          label="Template Name"
          name="templateName"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 15 }}
          rules={[{ required: true, message: 'Please enter the template name!' }]}
        >
          <Input placeholder="Enter template name" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 15 }}
        >
          <TextArea placeholder="Enter email description" />
        </Form.Item>

        {/* Subject */}
        <Form.Item
          label="Subject"
          name="subject"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 15 }}
          rules={[{ required: true, message: 'Please enter the subject!' }]}
        >
          <Input placeholder="Enter email subject" />
        </Form.Item>

        {/* Body */}
        <Form.Item
          label="Body"
          name="body"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 15 }}
        >
         <div style={{ width: "100%", height: 150 }}>
              <div ref={quillRef}  />
            </div>
        </Form.Item>

        {/* Buttons */}
        
        <div  style={{
                position: "sticky",
                bottom: 0,
                background: 'rgba(240, 242, 245, 0.9)', 
                padding: "10px 0",
                boxShadow: "0 -2px 5px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
                
                }}>
          <Button style={{ width: '120px', marginRight: '10px' }} onClick={handleCancel}>
            Cancel
          </Button>
          <Button style={{ width: '120px' }} type="primary" htmlType="submit">
            Save
          </Button>
        </div>
       
      </Form>
      </Card>
    </div>
  );
};

export default CreateEmailTemplate;